import { Injectable, OnDestroy, NgZone } from '@angular/core';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import {
  Room,
  RoomEvent,
  Track,
  RemoteParticipant,
  LocalParticipant,
  RemoteTrack,
  ConnectionState,
  RoomOptions,
} from 'livekit-client';
import { environment } from '../../../../environments/environment';

export interface ParticipantMedia {
  identity: string;
  name: string;
  branchName?: string;
  professionalTitle?: number | string | null;
  isSpeaking: boolean;
  isMicEnabled: boolean;
  isCameraEnabled: boolean;
  isScreenShareEnabled: boolean;
  videoElement?: HTMLVideoElement;
  audioElement?: HTMLAudioElement;
}

export interface RoomConnectionState {
  isConnected: boolean;
  isConnecting: boolean;
  connectionState: ConnectionState | null;
}

@Injectable({ providedIn: 'root' })
export class LiveKitRoomService implements OnDestroy {
  private room?: Room;
  private readonly destroy$ = new Subject<void>();

  private readonly connectionState$ = new BehaviorSubject<RoomConnectionState>({
    isConnected: false,
    isConnecting: false,
    connectionState: null,
  });

  private readonly localParticipant$ =
    new BehaviorSubject<ParticipantMedia | null>(null);

  private readonly remoteParticipants$ = new BehaviorSubject<
    ParticipantMedia[]
  >([]);

  private readonly participantConnected$ = new Subject<ParticipantMedia>();
  private readonly participantDisconnected$ = new Subject<string>();

  constructor(private ngZone: NgZone) {}

  getConnectionState$(): Observable<RoomConnectionState> {
    return this.connectionState$.asObservable();
  }

  getLocalParticipant$(): Observable<ParticipantMedia | null> {
    return this.localParticipant$.asObservable();
  }

  getRemoteParticipants$(): Observable<ParticipantMedia[]> {
    return this.remoteParticipants$.asObservable();
  }

  onParticipantConnected(): Observable<ParticipantMedia> {
    return this.participantConnected$.asObservable();
  }

  onParticipantDisconnected(): Observable<string> {
    return this.participantDisconnected$.asObservable();
  }

  getRoom(): Room | undefined {
    return this.room;
  }

  async connectToRoom(
    serverUrl: string | undefined | null,
    token: string,
    options?: { video?: boolean; audio?: boolean }
  ): Promise<void> {
    await this.disconnectFromRoom();

    let url = serverUrl;
    if (!url) {
      throw new Error(
        'LiveKit server URL is not provided and not found in environment'
      );
    }

    // Force 127.0.0.1 for Safari loopback stability
    if (url.includes('localhost')) {
      url = url.replace('localhost', '127.0.0.1');
    }

    console.log(
      `[LiveKit] OS Check: ${navigator.platform}, UA: ${navigator.userAgent}`
    );
    console.log(`[LiveKit] Secure Context: ${window.isSecureContext}`);
    console.log(`[LiveKit] RTCPeerConnection: ${!!window.RTCPeerConnection}`);

    console.log(`[LiveKit] Final Signaling URL: ${url}`);

    this.connectionState$.next({
      isConnected: false,
      isConnecting: true,
      connectionState: null,
    });

    const enableVideo = options?.video ?? true;
    const enableAudio = options?.audio ?? true;
    const wantsPublish = enableVideo || enableAudio;
    const roomOptions: RoomOptions = {
      adaptiveStream: false,
      dynacast: false,
      publishDefaults: {
        videoCodec: 'h264',
      },
    };

    return this.ngZone.runOutsideAngular(async () => {
      try {
        this.room = new Room(roomOptions);

        this.room.on(RoomEvent.ConnectionStateChanged, (state) => {
          console.log(`[LiveKit] Room Connection State: ${state}`);
        });

        this.setupRoomEventHandlers();

        console.log(`[LiveKit] Attempting connection to ${url}...`);

        try {
          await this.room.connect(url, token, {
            autoSubscribe: true,
          });
          console.log('[LiveKit] Connection successful (Attempt 1)');
        } catch (error: any) {
          console.warn('[LiveKit] Attempt 1 failed:', error?.message);

          if (error?.message?.includes('pc connection')) {
            console.log('[LiveKit] Retrying with autoSubscribe: true...');
            await this.room.disconnect();
            this.room = new Room(roomOptions);
            this.setupRoomEventHandlers();
            await this.room.connect(url, token, {
              autoSubscribe: true,
            });
            console.log('[LiveKit] Connection successful (Attempt 2)');
          } else {
            throw error;
          }
        }

        if (enableVideo) {
          await this.room.localParticipant
            .setCameraEnabled(true)
            .catch((e) => console.error('Video publish failed', e));
        }
        if (enableAudio) {
          await this.room.localParticipant
            .setMicrophoneEnabled(true)
            .catch((e) => console.error('Audio publish failed', e));
        }

        this.ngZone.run(() => {
          this.updateLocalParticipant();
          this.updateRemoteParticipants();

          this.connectionState$.next({
            isConnected: true,
            isConnecting: false,
            connectionState: this.room?.state || ConnectionState.Connected,
          });
        });
      } catch (error: any) {
        if (wantsPublish) {
          try {
            console.warn(
              '[LiveKit] Connect failed. Retrying in receive-only mode...'
            );
            await this.room?.disconnect();
            this.room = new Room(roomOptions);
            this.setupRoomEventHandlers();
            await this.room.connect(url, token, { autoSubscribe: true });
            console.log('[LiveKit] Connection successful (Receive-Only)');

            this.ngZone.run(() => {
              this.updateLocalParticipant();
              this.updateRemoteParticipants();
              this.connectionState$.next({
                isConnected: true,
                isConnecting: false,
                connectionState: this.room?.state || ConnectionState.Connected,
              });
            });
            return;
          } catch (fallbackError) {
            console.warn(
              '[LiveKit] Receive-only fallback failed:',
              fallbackError
            );
          }
        }

        console.error('[LiveKit] FATAL - Room.connect rejected:', error);

        const errorDetails = {
          name: error?.name,
          message: error?.message,
          code: error?.code,
          status: error?.status,
          reason: error?.reason,
          details: error?.details,
          stack: error?.stack,
        };
        console.error(
          '[LiveKit] Error Diagnostic Info:',
          JSON.stringify(errorDetails, null, 2)
        );

        if (error?.message?.includes('pc connection')) {
          console.error(
            '[LiveKit] WebRTC PeerConnection could not be established. Investigating ICE/STUN...'
          );
        }

        this.ngZone.run(() => {
          this.connectionState$.next({
            isConnected: false,
            isConnecting: false,
            connectionState: null,
          });
        });

        throw error;
      }
    });
  }

  /**
   * Disconnect from the current room
   */
  async disconnectFromRoom(): Promise<void> {
    if (!this.room) return;

    return this.ngZone.runOutsideAngular(async () => {
      try {
        await this.room?.disconnect();
      } catch (error) {
        console.warn('[LiveKit] Error during disconnect:', error);
      } finally {
        this.room = undefined;
        this.ngZone.run(() => {
          this.localParticipant$.next(null);
          this.remoteParticipants$.next([]);
          this.connectionState$.next({
            isConnected: false,
            isConnecting: false,
            connectionState: null,
          });
        });
      }
    });
  }

  /**
   * Explicitly resume the audio context for Safari/iOS
   */
  async startAudio(): Promise<void> {
    if (!this.room) return;
    try {
      console.log('[LiveKit] Explicitly starting audio...');
      await this.room.startAudio();
    } catch (error) {
      console.warn('[LiveKit] Failed to start audio context:', error);
    }
  }

  /**
   * Enable or disable the local microphone
   */
  async setMicrophoneEnabled(enabled: boolean): Promise<void> {
    if (!this.room?.localParticipant) return;

    await this.room.localParticipant.setMicrophoneEnabled(enabled);
    this.updateLocalParticipant();
  }

  /**
   * Enable or disable the local camera
   */
  async setCameraEnabled(enabled: boolean): Promise<void> {
    if (!this.room?.localParticipant) return;

    await this.room.localParticipant.setCameraEnabled(enabled);
    this.updateLocalParticipant();
  }

  /**
   * Enable or disable screen sharing
   */
  async setScreenShareEnabled(enabled: boolean): Promise<void> {
    if (!this.room?.localParticipant) return;

    await this.room.localParticipant.setScreenShareEnabled(enabled);
    this.updateLocalParticipant();
  }

  /**
   * Get local video track element
   */
  getLocalVideoElement(): HTMLVideoElement | undefined {
    if (!this.room?.localParticipant) return undefined;

    const publication = this.room.localParticipant.getTrackPublication(
      Track.Source.Camera
    );
    if (!publication?.track) return undefined;

    const element = publication.track.attachedElements[0] as HTMLVideoElement;
    return element || undefined;
  }

  /**
   * Attach local video track to an element
   */
  attachLocalVideo(element: HTMLVideoElement): void {
    if (!this.room?.localParticipant) return;

    const publication = this.room.localParticipant.getTrackPublication(
      Track.Source.Camera
    );
    if (publication?.track) {
      publication.track.attach(element);
    }
  }

  /**
   * Attach remote participant's video to an element
   */
  attachParticipantVideo(
    participantIdentity: string,
    element: HTMLVideoElement
  ): void {
    if (!this.room) return;

    const participant = this.room.remoteParticipants.get(participantIdentity);
    if (!participant) return;

    const publication = participant.getTrackPublication(Track.Source.Camera);
    if (publication?.track) {
      (publication.track as RemoteTrack).attach(element);
    }
  }

  /**
   * Attach remote participant's audio to an element
   */
  attachParticipantAudio(
    participantIdentity: string,
    element: HTMLAudioElement
  ): void {
    if (!this.room) return;

    const participant = this.room.remoteParticipants.get(participantIdentity);
    if (!participant) return;

    const publication = participant.getTrackPublication(
      Track.Source.Microphone
    );
    if (publication?.track) {
      (publication.track as RemoteTrack).attach(element);
    }
  }

  /**
   * Set up room event handlers
   */
  private setupRoomEventHandlers(): void {
    if (!this.room) return;

    this.room.on(RoomEvent.ParticipantConnected, (participant) => {
      const media = this.mapParticipantToMedia(participant);
      this.participantConnected$.next(media);
      this.updateRemoteParticipants();
    });

    this.room.on(RoomEvent.ParticipantDisconnected, (participant) => {
      this.participantDisconnected$.next(participant.identity);
      this.updateRemoteParticipants();
    });

    this.room.on(RoomEvent.TrackSubscribed, () => {
      this.updateRemoteParticipants();
    });

    this.room.on(RoomEvent.TrackUnsubscribed, () => {
      this.updateRemoteParticipants();
    });

    this.room.on(RoomEvent.TrackMuted, () => {
      this.updateLocalParticipant();
      this.updateRemoteParticipants();
    });

    this.room.on(RoomEvent.TrackUnmuted, () => {
      this.updateLocalParticipant();
      this.updateRemoteParticipants();
    });

    this.room.on(RoomEvent.LocalTrackPublished, () => {
      this.updateLocalParticipant();
    });

    this.room.on(RoomEvent.LocalTrackUnpublished, () => {
      this.updateLocalParticipant();
    });

    this.room.on(RoomEvent.ActiveSpeakersChanged, (speakers) => {
      this.updateRemoteParticipants();
    });

    this.room.on(RoomEvent.Disconnected, () => {
      this.connectionState$.next({
        isConnected: false,
        isConnecting: false,
        connectionState: ConnectionState.Disconnected,
      });
    });

    this.room.on(RoomEvent.Reconnecting, () => {
      this.connectionState$.next({
        isConnected: false,
        isConnecting: true,
        connectionState: ConnectionState.Reconnecting,
      });
    });

    this.room.on(RoomEvent.Reconnected, () => {
      this.connectionState$.next({
        isConnected: true,
        isConnecting: false,
        connectionState: ConnectionState.Connected,
      });
    });
  }

  /**
   * Update local participant state
   */
  private updateLocalParticipant(): void {
    if (!this.room?.localParticipant) {
      this.localParticipant$.next(null);
      return;
    }

    const media = this.mapLocalParticipantToMedia(this.room.localParticipant);
    this.localParticipant$.next(media);
  }

  /**
   * Update remote participants list
   */
  private updateRemoteParticipants(): void {
    if (!this.room) {
      this.remoteParticipants$.next([]);
      return;
    }

    const participants: ParticipantMedia[] = [];
    this.room.remoteParticipants.forEach((participant) => {
      participants.push(this.mapParticipantToMedia(participant));
    });

    this.remoteParticipants$.next(participants);
  }

  /**
   * Map local participant to media state
   */
  private mapLocalParticipantToMedia(
    participant: LocalParticipant
  ): ParticipantMedia {
    const cameraPublication = participant.getTrackPublication(
      Track.Source.Camera
    );
    const micPublication = participant.getTrackPublication(
      Track.Source.Microphone
    );
    const screenPublication = participant.getTrackPublication(
      Track.Source.ScreenShare
    );

    return {
      identity: participant.identity,
      name: participant.name || participant.identity,
      isSpeaking: participant.isSpeaking,
      isMicEnabled: micPublication?.isMuted === false,
      isCameraEnabled: cameraPublication?.isMuted === false,
      isScreenShareEnabled: !!screenPublication && !screenPublication.isMuted,
    };
  }

  /**
   * Map remote participant to media state
   */
  private mapParticipantToMedia(
    participant: RemoteParticipant
  ): ParticipantMedia {
    const cameraPublication = participant.getTrackPublication(
      Track.Source.Camera
    );
    const micPublication = participant.getTrackPublication(
      Track.Source.Microphone
    );
    const screenPublication = participant.getTrackPublication(
      Track.Source.ScreenShare
    );

    return {
      identity: participant.identity,
      name: participant.name || participant.identity,
      isSpeaking: participant.isSpeaking,
      isMicEnabled: micPublication?.isMuted === false,
      isCameraEnabled: cameraPublication?.isMuted === false,
      isScreenShareEnabled: !!screenPublication && !screenPublication.isMuted,
    };
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.disconnectFromRoom();
  }
}
